import { useCallback, useMemo } from 'react'

import { useFirm } from '~/components/hooks/useFirm'
import {
  Feature,
  FirmProfileFragment,
  useFetchFirmFeatureFlags,
  useFetchUserFeatureFlags,
} from '~/graphql/generated/query.types'
import { featureFlagConfigs, isForceEnabledDemoFirm } from '~/util/featureFlags'

export function useFeatureFlag(feature: Feature) {
  const firm = useFirm()
  const featureFlagsForFirm = useFetchFirmFeatureFlags()
  const featureFlagsForUser = useFetchUserFeatureFlags()

  const isFeatureEnabled = useMemo(
    () => _getIsFeatureEnabledForUser(featureFlagsForFirm, featureFlagsForUser, feature, firm),
    [featureFlagsForFirm, featureFlagsForUser, feature, firm]
  )

  return isFeatureEnabled
}

export function useFeatureFlags(): (feature: Feature) => boolean {
  const firm = useFirm()
  const featureFlagsForFirm = useFetchFirmFeatureFlags()
  const featureFlagsForUser = useFetchUserFeatureFlags()

  const getIsFeatureEnabled = useCallback(
    (feature: Feature) =>
      _getIsFeatureEnabledForUser(featureFlagsForFirm, featureFlagsForUser, feature, firm),
    [featureFlagsForFirm, featureFlagsForUser, firm]
  )
  return getIsFeatureEnabled
}

/**
 * Whether firm and user feature-flag queries have finished loading.
 * Until this is true, {@link useFeatureFlags} / {@link useFeatureFlag} treat
 * unknown flags as disabled — callers that redirect based on a flag should wait.
 */
export function useAreFeatureFlagsLoaded(): boolean {
  const featureFlagsForFirm = useFetchFirmFeatureFlags()
  const featureFlagsForUser = useFetchUserFeatureFlags()
  return !featureFlagsForFirm.loading && !featureFlagsForUser.loading
}

function _getIsFeatureEnabledForUser(
  featureFlagsForFirm: ReturnType<typeof useFetchFirmFeatureFlags>,
  featureFlagsForUser: ReturnType<typeof useFetchUserFeatureFlags>,
  feature: Feature,
  firm: FirmProfileFragment
): boolean {
  if (isForceEnabledDemoFirm(firm) && featureFlagConfigs[feature].forceEnabledForDemoFirms) {
    return true
  }
  const firmFlagIsEnabled =
    featureFlagsForFirm.data?.fetchFirmFeatureFlags.find(f => f.feature === feature)?.isEnabled ??
    false
  if (firmFlagIsEnabled) return true
  const userFlagIsEnabled = featureFlagsForUser.data?.fetchUserFeatureFlags.find(
    f => f.feature === feature
  )?.isEnabled
  return userFlagIsEnabled ?? false
}
